"use client";

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { Check } from "@/components/ui/icons";
import { QSH, QSH_DEMO, type QshIntent } from "@/content/quiksheets";
import { cn } from "@/lib/utils";
import { LEAD_ENDPOINT as ENDPOINT } from "@/lib/lead-endpoint";

/**
 * Submissions go to this site's own endpoint, which stores the lead and emails it.
 * Set NEXT_PUBLIC_FORM_DISABLED=1 to keep the form in preview mode (validates and shows
 * the confirmation, stores nothing) for a build that should not reach the sales inbox.
 */

const EASE = [0.23, 1, 0.32, 1] as const;

const field =
  "h-11 w-full rounded-[12px] bg-white px-3.5 text-[14px] outline-none ring-1 ring-inset ring-ink/[0.08] transition-shadow placeholder:text-faint focus:ring-2 focus:ring-accent aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[#c2413b]";

type Errors = Partial<Record<"name" | "email" | "phone" | "company", string>>;

function validate(d: Record<string, string>): Errors {
  const e: Errors = {};
  if (!d.name?.trim()) e.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email?.trim() ?? "")) e.email = "Please enter a valid work email.";
  const p = d.phone?.trim() ?? "";
  if (p && !(/^[0-9+().\-\s]{7,20}$/.test(p) && p.replace(/\D/g, "").length >= 7)) e.phone = "Please enter a valid phone number, or leave it blank.";
  if (!d.company?.trim()) e.company = "Please enter your company.";
  return e;
}

function Field({ id, label, error, optional, children }: { id: string; label: string; error?: string; optional?: boolean; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[12px] font-medium text-ink/70">
        {label} {optional ? <span className="font-normal text-faint">(optional)</span> : <span className="text-[#c2413b]">*</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-err`} className="mt-1 text-[11.5px] text-[#c2413b]">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * "See QuikSheets with your team in mind". The "I want to" choice rewrites the heading, lead
 * and button; any link on the page carrying `data-intent` pre-selects it.
 */
export function DemoForm() {
  const [intent, setIntent] = useState<QshIntent>("consolidate");
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [firstName, setFirstName] = useState("");
  const copy = QSH_DEMO.copy[intent];

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-intent]");
      const v = el?.dataset.intent as QshIntent | undefined;
      if (v && v in QSH_DEMO.copy) setIntent(v);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
    const errs = validate(data);
    setErrors(errs);
    if (Object.keys(errs).length) {
      form.querySelector<HTMLElement>(`[name="${Object.keys(errs)[0]}"]`)?.focus();
      return;
    }
    setStatus("sending");
    try {
      if (ENDPOINT) {
        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, intent, source: "quiksheets" }),
        });
        // The endpoint reports why it failed; a bare status check would hide a lost lead.
        const reply = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !reply?.ok) throw new Error(reply?.error ?? String(res.status));
      } else {
        await new Promise((r) => setTimeout(r, 700));
      }
      setFirstName(data.name.trim().split(" ")[0]);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  const err = (k: keyof Errors) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `qsh-${k}-err` : undefined });

  return (
    <section id="demo" aria-labelledby="qsh-demo-title" className="scroll-mt-28 pt-[clamp(110px,11vw,170px)]">
      <div className="wrap grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-14">
        <div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={intent}
              initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -8, filter: "blur(6px)" }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <Eyebrow>{copy.eyebrow}</Eyebrow>
              <h2 id="qsh-demo-title" className="mt-4 max-w-[13em] text-[clamp(1.75rem,2.35vw,2.3rem)] font-[450] leading-[1.2] tracking-[-0.035em]">
                {copy.title}
              </h2>
              <p className="mt-4 max-w-[44ch] text-[14px] leading-[1.6] text-muted">{copy.lead}</p>
            </motion.div>
          </AnimatePresence>
          <ol className="mt-9 space-y-3">
            {QSH_DEMO.agenda.map((a, i) => (
              <li key={a.title} className="flex gap-4 rounded-[18px] bg-panel p-4">
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ink text-[12px] font-medium text-white">{i + 1}</span>
                <span>
                  <span className="block text-[14px] font-medium">{a.title}</span>
                  <span className="block text-[14px] text-muted">{a.text}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <AnimatedCard y={70} className="rounded-[28px] bg-panel p-5 md:p-8">
          <AnimatePresence mode="wait" initial={false}>
            {status === "sent" ? (
              <motion.div
                key="sent"
                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                role="status"
                className="flex min-h-[460px] flex-col items-center justify-center text-center"
              >
                <span className="grid size-14 place-items-center rounded-full bg-mint text-ink">
                  <Check className="size-6" strokeWidth={2.4} />
                </span>
                <h3 className="mt-6 text-[24px] font-[450] tracking-[-0.03em]">{copy.ok}</h3>
                <p className="mt-2 max-w-[38ch] text-[14px] text-muted">
                  Thanks{firstName ? `, ${firstName}` : ""}. {copy.msg}
                </p>
                {!ENDPOINT && <p className="mt-2 text-[11.5px] text-faint">Preview mode — this build does not deliver submissions.</p>}
                <a href={QSH.links.register} className="btn btn-mint mt-6 h-10 px-5 text-[14px]">
                  <span className="btn-label" data-btn-label="">
                    <span data-text="Start free meanwhile">Get QuikSheets meanwhile</span>
                  </span>
                </a>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={onSubmit} noValidate exit={{ opacity: 0, filter: "blur(6px)" }} className="grid gap-4 sm:grid-cols-2">
                <fieldset className="sm:col-span-2">
                  <legend className="mb-2 text-[12px] font-medium text-ink/70">I want to</legend>
                  {/* Three across only from xl, where the card stops growing and a chip is wide enough to
                      hold its label on one line; below that they stack, which beats three cramped
                      two-line columns. */}
                  <div className="grid grid-cols-1 gap-1 rounded-[16px] bg-white p-1 xl:grid-cols-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                    {QSH_DEMO.intents.map((o) => {
                      const on = o.value === intent;
                      return (
                        <label key={o.value} className="relative cursor-pointer rounded-[12px] px-2 py-2.5 text-center">
                          <input type="radio" name="intent" value={o.value} checked={on} onChange={() => setIntent(o.value)} className="sr-only" />
                          {on && (
                            <motion.span
                              layoutId="qsh-demo-intent"
                              className="absolute inset-0 rounded-[12px] bg-ink"
                              transition={{ type: "spring", duration: 0.4, bounce: 0.12 }}
                            />
                          )}
                          <span className={cn("relative block text-[14px] font-medium transition-colors", on ? "text-white" : "text-ink")}>
                            {o.label}
                          </span>
                          <span className={cn("relative block text-[10.5px] transition-colors", on ? "text-white/60" : "text-muted")}>{o.meta}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
                <Field id="qsh-name" label="Full name" error={errors.name}>
                  <input id="qsh-name" name="name" autoComplete="name" className={field} placeholder="Jane Doe" {...err("name")} />
                </Field>
                <Field id="qsh-email" label="Work email" error={errors.email}>
                  <input id="qsh-email" name="email" type="email" autoComplete="email" className={field} placeholder="jane@company.com" {...err("email")} />
                </Field>
                <Field id="qsh-phone" label="Contact number" optional error={errors.phone}>
                  <input id="qsh-phone" name="phone" type="tel" autoComplete="tel" className={field} placeholder="+91 98765 43210" {...err("phone")} />
                </Field>
                <Field id="qsh-company" label="Company" error={errors.company}>
                  <input id="qsh-company" name="company" autoComplete="organization" className={field} placeholder="Company name" {...err("company")} />
                </Field>
                <Field id="qsh-size" label="Team size" optional>
                  <select id="qsh-size" name="size" defaultValue="" className={cn(field, "appearance-none")}>
                    <option value="">Select…</option>
                    {QSH_DEMO.sizes.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field id="qsh-current" label="Where your data lives today" optional>
                  <select id="qsh-current" name="current" defaultValue="" className={cn(field, "appearance-none")}>
                    <option value="">Select…</option>
                    {QSH_DEMO.current.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <label htmlFor="qsh-focus" className="mb-1.5 block text-[12px] font-medium text-ink/70">
                    What should we focus on? <span className="font-normal text-faint">(optional)</span>
                  </label>
                  <textarea
                    id="qsh-focus"
                    name="focus"
                    rows={3}
                    className={cn(field, "h-auto resize-none py-3")}
                    placeholder="Reports you maintain, data sources, who collaborates…"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
                  <p className="text-[11.5px] text-muted">{status === "error" ? "Something went wrong — please try again." : "We reply within one business day."}</p>
                  <button type="submit" disabled={status === "sending"} className="btn btn-mint h-11 px-6 text-[14px] disabled:opacity-60">
                    <span className="btn-label" data-btn-label="">
                      <span data-text={status === "sending" ? "Sending…" : copy.button}>{status === "sending" ? "Sending…" : copy.button}</span>
                    </span>
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </AnimatedCard>
      </div>
    </section>
  );
}
